<script setup lang="ts">
import IconBox from '../shared/IconBox.vue'
import AccordeonElementalsListTrigger from './AccordeonElementalsListTrigger.vue'
import { useSidebarStore } from '~~/src/stores/sidebar'
import Accordeon, { type AccordeonEmits, type Props as AccordeonProps } from '@/components/Accordion/index.vue'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import type { Elemental } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
import ElemetalIconPicker from '~~/src/components/Elemental/ElemetalIconPicker/index.vue'
import { useDragAndDropStore } from '~~/src/stores/drag-and-drop'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'

type Props = {
  list: List | Elemental
  listId?: string | number
  parentListId?: string | number
  listTitle: string
  isMain?: boolean
  nPrompts: number
  iconBoxClass?: string
  elementalsType: ElementalTypeEnum
} & AccordeonProps

const props = defineProps<Props>()

const emits = defineEmits<Emits>()

defineOptions({
  name: 'AccordeonElementalsList',
})

type Emits = AccordeonEmits & {
  (event: 'moved', from: number, to: number): void
  (event: 'droped', data: Elemental, index: number): void
}

const { isElementalOpen } = useSidebarStore()
const { state: store } = useDragAndDropStore()
const { state } = useElementalListsStore()

const accordeonRef = ref<InstanceType<typeof Accordeon> | null>(null)

const accordId = props.listId ? `${props.listId}` : `${props.list.id}`
const isOpen = isElementalOpen(accordId, props.parentListId
  ? `${props.parentListId}`
  : null)

const fn = useDebounceFn(() => {
  if (state.query === '')
    return
  const value = props.nPrompts
  if (accordeonRef.value)
    accordeonRef.value.toggle(value < 1)
}, 100)

watch(() => state.query, () => {
  fn()
})

const wrapperRef = computed(() => (accordeonRef.value?.wrapperRef as HTMLDivElement) ?? null)
const contentRef = ref<HTMLDivElement | null>(null)
const previewRef = ref<HTMLDivElement | null>(null)

const { isHover } = useDropzone({
  container: wrapperRef,
  previewArea: contentRef,
  previewComponent: previewRef,
  onDrop: (data, index) => {
    accordeonRef.value?.toggle(true)
    emits('droped', data as Elemental, index)
  },
  onMove: (from, to) => {
    emits('moved', from, to)
  },
  disabled: computed(() => props.isDisabled || props.isMain),
})

const timer = ref<ReturnType<typeof setTimeout> | null>(null)
watch(isHover, (value) => {
  if (value)
    timer.value = setTimeout(() => accordeonRef.value?.toggle(true), 1000)
  else if (timer.value)
    clearTimeout(timer.value)
})

const elementalData = computed(() => (store.dragElementData as {
  listId?: string | number
  elemental: Elemental | null
})?.elemental)
</script>

<template>
  <Accordeon
    ref="accordeonRef"
    v-bind="$props"
    :is-open="isOpen" :accordion-id="accordId"
    class="rounded-lg transition-colors border-transparent" :class="[$style.accordeon, {
      'mb-3': isOpen,
    }]"
    ignore-events @on-close="$emit('onClose')" @on-open="$emit('onOpen')"
    @on-toggle="$emit(`onToggle`, $event)"
  >
    <template #header>
      <slot name="header">
        <AccordeonElementalsListTrigger
          :parent-list-id="parentListId"
          :elementals-type="elementalsType" :list="list" :list-id="accordId"
          :is-main="isMain" :title="listTitle" :n-prompts="nPrompts" :icon-box-class="iconBoxClass" :class="{
            'bg-grayLighter': isHover,
          }"
        >
          <template #icon>
            <slot name="icon" />
          </template>
        </AccordeonElementalsListTrigger>
      </slot>
    </template>
    <template #content>
      <div class="flex flex-col gap-1">
        <div ref="contentRef" class="pl-2 m-1" :class="[$style['no-scrollbar'], { 'bg-grayLighter/50': isHover }]">
          <li
            v-if="elementalData && isHover" ref="previewRef"
            class="flex preview text-darkGray flex-nowrap justify-left p-2 gap-2 items-center relative bg-grayLighter  border-1 border-softGray rounded"
          >
            <IconBox>
              <ElemetalIconPicker
                :type="elementalData?.isList ? ElementalTypeEnum.List : elementalData.elementalTypeId!" w-6 h-6
              />
            </IconBox>
            <p text-sm text-softBlack line-clamp-1 text-start>
              {{ elementalData?.title }}
            </p>
          </li>
          <slot name="content" :is-hover="isHover" />
        </div>
        <div class="px-2">
          <slot name="footer" />
        </div>
      </div>
    </template>
  </Accordeon>
</template>

<style module lang="css">
.no-scrollbar {
  ::-webkit-scrollbar {
    display: none;
  }
}

.accordeon {
  &[aria-expanded='true'] {
    @apply border-1 border-softGray;
  }
}
</style>
